import {Badge} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Text} from 'src/design/atoms/Text';
import {IconInfoCircle} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {BadgeTemplateSimpleResponse} from 'src/api/ProactApiClient';
import {UserTypeEnum} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {
  useDeleteBadgeTemplateMutation,
  useGetBadgeTemplateListQuery,
} from 'src/redux/queries/badgeTemplates';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {BadgeTemplateEditorModal} from '../components/BadgeTemplateEditorModal';
import {BadgeTemplateVersionHistoryModal} from '../components/BadgeTemplateVersionHistoryModal';

export interface BadgeTemplatesTabProps {
  // Target instance being managed — every BadgeTemplate call carries it
  // explicitly (integration-token pattern) since super-admins manage arbitrary
  // instances, not their own current instance.
  instanceId: number;
}

const userTypeLabels = (userTypes: UserTypeEnum[] | undefined): string => {
  if (!userTypes || userTypes.length === 0) return 'Any';
  return userTypes
    .map(type => (type === UserTypeEnum.Employee ? 'Employee' : 'Contractor'))
    .join(', ');
};

export const BadgeTemplatesTab: React.FC<BadgeTemplatesTabProps> = ({
  instanceId,
}) => {
  const {setAlert} = useAlert();
  const [editingTemplateId, setEditingTemplateId] = useState<
    number | undefined
  >();
  const [editorOpen, setEditorOpen] = useState(false);
  const [historyTarget, setHistoryTarget] =
    useState<BadgeTemplateSimpleResponse | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<BadgeTemplateSimpleResponse | null>(null);

  const {data, isLoading, isError, isFetching, refetch} =
    useGetBadgeTemplateListQuery(
      {
        instanceId,
        start: 0,
        length: 1000,
        sortColumn: 'name',
        sortDirection: 'asc',
      },
      {refetchOnMountOrArgChange: true}
    );

  const [deleteBadgeTemplate, {isLoading: isDeleting}] =
    useDeleteBadgeTemplateMutation();

  const rows = data?.data ?? [];

  const handleCreate = () => {
    setEditingTemplateId(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (template: BadgeTemplateSimpleResponse) => {
    setEditingTemplateId(template.id);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTemplateId(undefined);
  };

  const handleEditorSaved = () => {
    setEditorOpen(false);
    setEditingTemplateId(undefined);
    void refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBadgeTemplate({id: deleteTarget.id, instanceId}).unwrap();
      setAlert(`"${deleteTarget.name}" has been deleted`, 'success');
      setDeleteTarget(null);
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setDeleteTarget(null);
    }
  };

  const columns = useMemo<MRT_ColumnDef<BadgeTemplateSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({row}) => (
          <Text size="sm" fw={500}>
            {row.original.name}
          </Text>
        ),
      },
      {
        id: 'userTypes',
        header: 'Targeted user types',
        accessorFn: row => userTypeLabels(row.targetingMetadata?.userTypes),
      },
      {
        id: 'version',
        header: 'Version',
        accessorFn: row => row.version,
        Cell: ({row}) => (
          <Badge color="blue" variant="light">
            v{row.original.version}
          </Badge>
        ),
      },
      {
        id: 'updatedAt',
        accessorFn: row => (row.updatedAt ? new Date(row.updatedAt) : null),
        filterVariant: 'date',
        header: 'Last Updated',
        Cell: ({row}) => (
          <>
            {row.original.updatedAt
              ? String(row.original.updatedAt).toShortDate()
              : '—'}
          </>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'badge-templates-tab',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: false,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.Edit onClick={() => handleEdit(row.original)} />
          <TableRowActions.Menu>
            <TableRowActions.MenuItem
              onClick={() => setHistoryTarget(row.original)}>
              Version History
            </TableRowActions.MenuItem>
            <TableRowActions.MenuItem
              color="red"
              onClick={() => setDeleteTarget(row.original)}>
              Delete
            </TableRowActions.MenuItem>
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <TableToolbar.Action variant="create" onClick={handleCreate}>
              New Template
            </TableToolbar.Action>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount: rows.length,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      <PageHeader>
        <PageHeader.Title subtitle="Manage the HTML badge templates used to print personnel badges for this instance">
          Badge Templates
        </PageHeader.Title>
      </PageHeader>

      {!isLoading && !isError && rows.length === 0 && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle />}
          title="No badge templates yet"
          mb="md">
          <Text size="sm">
            Create a template to enable on-demand badge generation and printing
            for this instance's personnel.
          </Text>
        </Alert>
      )}

      {table}

      <BadgeTemplateEditorModal
        opened={editorOpen}
        instanceId={instanceId}
        templateId={editingTemplateId}
        onClose={handleEditorClose}
        onSaved={handleEditorSaved}
      />

      <BadgeTemplateVersionHistoryModal
        opened={historyTarget !== null}
        instanceId={instanceId}
        templateId={historyTarget?.id}
        templateName={historyTarget?.name}
        onClose={() => setHistoryTarget(null)}
      />

      <ConfirmDialog
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Badge Template?"
        body={`This will permanently delete "${
          deleteTarget?.name ?? ''
        }" and all of its versions. Personnel badges already generated from it are unaffected, but it can no longer be used to generate new ones. This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete Template"
        buttonTextNo="Cancel"
        confirmationButtonDisabled={isDeleting}
      />
    </>
  );
};
